import React from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Icon, Input, Table } from 'semantic-ui-react';
import { RootConsumer } from '../../RootComponent';
import { GlobalNotifications, Intent, MobileModal } from '../../Modules';
import axios from 'axios';
import { produce } from 'immer';
import styles from '../Playlist.scss';

class SelectSongs extends React.Component {
  static propTypes = {
    addToPlaylist: PropTypes.func.isRequired,
    authenticateSpotify: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedPlaylistId: PropTypes.string.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      items: null,
      query: '',
      submitting: false
    };
    this.onSuccess = this.onSuccess.bind(this);
    this.onError = this.onError.bind(this);
    this.getPlaylistItems = this.getPlaylistItems.bind(this);
    this.getTotalSelected = this.getTotalSelected.bind(this);
    this.addSelectedSongs = this.addSelectedSongs.bind(this);
    this.filterByQuery = this.filterByQuery.bind(this);
    this.selectAll = this.selectAll.bind(this);
    this.selectNone = this.selectNone.bind(this);
    this.toggleSong = this.toggleSong.bind(this);
  }

  componentWillMount() {
    this.getPlaylistItems({ playlistId: this.props.selectedPlaylistId });
  }

  onSuccess() {
    this.setState({ submitting: false });
    this.props.onClose();
  }

  onError() {
    this.setState({ submitting: false });
  }

  getPlaylistItems({ playlistId, limit = 100, offset = 0, items = [] }) {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('spotify_access_token')}`
      },
      params: { limit, offset }
    };
    axios
      .get(`https://api.spotify.com/v1/playlists/${playlistId}/tracks`, config)
      .then(response => {
        if (response.data.next) {
          this.getPlaylistItems({
            limit,
            playlistId,
            offset: offset + limit,
            items: items.concat(response.data.items)
          });
        } else {
          this.setState({
            items: items
              .concat(response.data.items)
              .map(item => Object.assign({ selected: true }, item)),
            selectPlaylistOpen: true
          });
        }
      })
      .catch(error => {
        if (error.response.status === 401) {
          this.props.authenticateSpotify(() =>
            this.getPlaylistItems({ playlistId })
          );
        } else {
          GlobalNotifications.show({
            message: 'Unable to get playlist from Spotify',
            intent: Intent.DANGER
          });
        }
      });
  }

  getTotalSelected() {
    return this.state.items.filter(item => item.selected).length;
  }

  addSelectedSongs(e) {
    const { items } = this.state;
    const tracks = items
      .filter(item => item.selected)
      .map(({ track }) => track);
    const songs = tracks.map(track => ({
      name: track.name,
      album: track.album.name,
      artist: track.artists[0].name,
      duration_ms: track.duration_ms,
      isrc: track.external_ids.isrc,
      song_images: {
        edges: track.album.images.map(image => ({ node: image }))
      },
      external_data: JSON.stringify(track),
      external_source: 'spotify',
      external_id: track.id
    }));
    this.setState({ submitting: true });
    this.props.addToPlaylist(e, songs, null, this.onSuccess, this.onError);
  }

  toggleSong(trackId) {
    this.setState(
      produce(draft => {
        draft.items.map(item => {
          if (item.track.id === trackId) {
            /* eslint-disable no-param-reassign */
            item.selected = !item.selected;
          }
          return item;
        });
      })
    );
  }

  selectAll() {
    this.setState({
      items: this.state.items.map(item =>
        Object.assign({}, item, { selected: true })
      )
    });
  }

  selectNone() {
    this.setState({
      items: this.state.items.map(item =>
        Object.assign({}, item, { selected: false })
      )
    });
  }

  filterByQuery(items) {
    const { query } = this.state;
    return items.filter(
      item => item.track.name.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }

  render() {
    const { goBack } = this.props;
    const { items, query, submitting } = this.state;
    return (
      <RootConsumer>
        {({ isMobile }) =>
          <div>
            {isMobile &&
              <MobileModal.Header>
                <div className='menu'>
                  <a className='item' onClick={goBack}>
                    <Icon name='chevron left' />
                  </a>
                </div>
                <div className='menu right'>
                  <a
                    className='item'
                    onClick={submitting ? null : this.addSelectedSongs}
                  >
                    {submitting ? 'Importing...' : 'Import'}
                  </a>
                </div>
              </MobileModal.Header>}
            <h3>Select the songs you would like to import</h3>
            <br />
            <div
              style={{ display: isMobile ? null : 'flex', marginBottom: '1em' }}
            >
              <div style={{ flex: 1 }}>
                <Input
                  name='search'
                  type='text'
                  size='small'
                  icon={{
                    name: query === '' ? 'search' : 'close',
                    onClick: () => this.setState({ query: '' }),
                    link: query !== ''
                  }}
                  placeholder='Filter...'
                  onChange={e => this.setState({ query: e.target.value })}
                  value={query}
                  style={{ marginBottom: '1em' }}
                />
              </div>
              <div>
                <a onClick={this.selectAll}>Select All</a> &nbsp;/&nbsp;&nbsp;
                <a onClick={this.selectNone}>Deselect All</a>
                {items &&
                  <div
                    style={
                      isMobile ? { marginTop: '0.5em' } : { textAlign: 'right' }
                    }
                    className={styles.muted}
                  >
                    Total Selected: {this.getTotalSelected()}
                  </div>}
              </div>
            </div>
            <div
              style={isMobile ? {} : { maxHeight: '300px', overflowY: 'auto' }}
            >
              <Table striped stackable={false}>
                <Table.Body>
                  {items &&
                    this.filterByQuery(items).map(item =>
                      <Table.Row
                        key={item.track.id}
                        onClick={() => this.toggleSong(item.track.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Table.Cell>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <div style={{ flex: 1 }}>
                              <strong>
                                {item.track.name}
                              </strong>
                              <p className={styles.muted}>
                                {item.track.artists[0].name}
                              </p>
                            </div>
                            <div>
                              <Checkbox checked={item.selected} />
                            </div>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                </Table.Body>
              </Table>
            </div>
            {!items && <div>loading</div>}
            {!isMobile &&
              <div style={{ marginTop: '2em', textAlign: 'right' }}>
                <span
                  className={styles.muted}
                  style={{ cursor: 'pointer', marginRight: '2em' }}
                  onClick={goBack}
                >
                  Back
                </span>
                <Button
                  primary
                  content={submitting ? 'Importing...' : 'Import'}
                  disabled={submitting}
                  onClick={this.addSelectedSongs}
                />
              </div>}
          </div>}
      </RootConsumer>
    );
  }
}

export default SelectSongs;



// WEBPACK FOOTER //
// ./webpack/components/Playlist/Import/SelectSongs.js